import React from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import type { MonthlyAddOnInterval } from 'fym-shared/src/music24/monthlyAddOns';
import { TypedMemo } from '../../../common/types';
import { LoadingIcon } from '../../../common/views/LoadingIcon';
import { formatAddOnCents } from './addOnFormat';
import { monthlyAddOnMessages as messages } from './monthlyAddOnMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the add-ons panel; each part uses its own slice of it */
import styles from './MonthlyAddOns.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The `<b>` tags of the sum line. Outside the component, so React keeps one function. */
const bold = (chunks: React.ReactNode[]) => <b>{chunks}</b>;

interface AddOnTotalProps {
  planName: string;
  planPeriodCents: number | null;
  addOnsCents: number;
  totalCents: number | null;
  monthlyCents: number | null;
  interval: MonthlyAddOnInterval;
  changed: boolean;
  saving: boolean;
  onSave: () => void;
  onReset: () => void;
}

/** The plan plus the add-ons, the total and Reset / Save (design monthly-addons a1, a2, b6). */
const AddOnTotalComponent: React.FC<AddOnTotalProps> = ({
  planName,
  planPeriodCents,
  addOnsCents,
  totalCents,
  monthlyCents,
  interval,
  changed,
  saving,
  onSave,
  onReset,
}) => {
  const intl = useIntl();
  return (
    <div className={styles.total}>
      <div>
        {planPeriodCents !== null && (
          <div className={styles.sum}>
            {intl.formatMessage(messages.sum, {
              plan: planName,
              planPrice: formatAddOnCents(intl, planPeriodCents),
              addOnsPrice: formatAddOnCents(intl, addOnsCents),
              b: bold,
            })}
          </div>
        )}
        {totalCents !== null && (
          <div className={styles.grand} data-testid="addon-total">
            {formatAddOnCents(intl, totalCents)}
            <small>
              {interval === 'yearly'
                ? intl.formatMessage(messages.totalYearSmall, { monthly: formatAddOnCents(intl, monthlyCents ?? 0) })
                : intl.formatMessage(messages.aMonth)}
            </small>
          </div>
        )}
      </div>
      <div className={styles.buttons}>
        <Button color="link" className="text-secondary" onClick={onReset} disabled={!changed || saving}>
          {intl.formatMessage(messages.reset)}
        </Button>
        <Button color="primary" onClick={onSave} disabled={!changed || saving} data-testid="addon-save">
          {saving && (
            <span className="me-2">
              <LoadingIcon small />
            </span>
          )}
          {intl.formatMessage(saving ? messages.saving : messages.save)}
        </Button>
      </div>
    </div>
  );
};

export const AddOnTotal = TypedMemo(AddOnTotalComponent);
